import React from 'react';

import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';

import type { NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import { AppIcon } from '../app-icon/app-icon';
import { IconStack } from './icon-stack';
import { getAppFromSource } from './utils';

const DEFAULT_MAX_SOURCES_IN_STACK = 3;

type SourcesStackProps = {
	children?: React.ReactNode;
	maxSourcesInStack?: number;
	sources: NLPSearchResultSource[];
	showNumAddtlSources?: boolean;
	testId?: string;
	addEditorStyleOverrides?: boolean;
	/** When true, applies the notebook-style shuffled look to the icon stack. */
	shuffled?: boolean;
};

export function SourcesStack({
	children,
	maxSourcesInStack,
	sources,
	showNumAddtlSources,
	testId,
	addEditorStyleOverrides,
	shuffled = false,
}: SourcesStackProps): React.JSX.Element | null {
	const providerIcons = React.Children.toArray(children);
	if (sources.length === 0 && providerIcons.length === 0) {
		return null;
	}

	const sourcesForIconStack: NLPSearchResultSource[] = [];
	const sourceProductsMap: Record<string, boolean> = {};
	maxSourcesInStack = maxSourcesInStack ?? DEFAULT_MAX_SOURCES_IN_STACK;
	const visibleProviderIcons = providerIcons.slice(0, maxSourcesInStack);
	const maxAppIcons = maxSourcesInStack - visibleProviderIcons.length;

	for (let i = 0; i < sources.length; i++) {
		if (sourcesForIconStack.length >= maxAppIcons) {
			break;
		}
		const source = sources[i];
		const appKey = getAppFromSource(source.product, source.url) || 'Unknown';

		if (!sourceProductsMap[appKey]) {
			sourcesForIconStack.push(source);
			sourceProductsMap[appKey] = true;
		}
	}

	const appIcons = sourcesForIconStack.map((source) => (
		<AppIcon
			// still need to convert product to correct subproduct e.g. for Microsoft
			app={getAppFromSource(source.product, source.url)}
			data-testid={`app-icon-${source.ari}`}
			key={source.ari}
			size="xxsmall"
		/>
	));

	// want to display the number of additional sources that are not included in the appIcons stack
	const numAddtlSources = sources.length - appIcons.length;

	return appIcons.length > 0 || visibleProviderIcons.length > 0 ? (
		<Inline alignBlock="center" space="space.050">
			<IconStack
				testId={testId}
				addEditorStyleOverrides={addEditorStyleOverrides}
				shuffled={shuffled}
			>
				{[...appIcons, ...visibleProviderIcons]}
			</IconStack>
			{showNumAddtlSources && numAddtlSources > 0 && (
				<Text color="color.text.subtle" size="small">
					+{numAddtlSources}
				</Text>
			)}
		</Inline>
	) : null;
}
